import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getBynderAccessToken, getPortalDomain } from 'packages/store/platform/platform.selectors';
import { getUser } from 'packages/store/user/user.selectors';
import portalService from 'packages/services/portalService';

/**
 * Bynder account-level features that are not available through Studio permissions.
 * These are fetched once from the portal features endpoint.
 */
export const BYNDER_PORTAL_FEATURES = {
    STUDIO_GEN_AI: 'a3d70e04-d9ac-4761-bec4-f38e8f83bc5f',
} as const;

export type BynderPortalFeature = (typeof BYNDER_PORTAL_FEATURES)[keyof typeof BYNDER_PORTAL_FEATURES];

type BynderFeaturesContextValue = {
    features: string[];
    isLoading: boolean;
    hasFeature: (feature: BynderPortalFeature) => boolean;
};

const BynderFeaturesContext = createContext<BynderFeaturesContextValue | undefined>(undefined);

export const BynderFeaturesProvider = ({ children }: { children: React.ReactNode }) => {
    const portalDomain = useSelector(getPortalDomain);
    const bynderAccessToken = useSelector(getBynderAccessToken);
    const { companies, currentCompany } = useSelector(getUser);

    const bynderAccountId = useMemo(
        () => companies.find((company) => company.id === currentCompany)?.bynderAccountId,
        [companies, currentCompany],
    );

    const [features, setFeatures] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!bynderAccessToken || !bynderAccountId) {
            return;
        }

        let isActive = true;
        setIsLoading(true);

        portalService
            .getPortalFeatures(portalDomain, bynderAccessToken, bynderAccountId)
            .then((result) => {
                if (isActive) {
                    setFeatures(Array.isArray(result?.features) ? result.features : []);
                }
            })
            .catch(() => {
                if (isActive) {
                    setFeatures([]);
                }
            })
            .finally(() => {
                if (isActive) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [portalDomain, bynderAccessToken, bynderAccountId]);

    const value = useMemo<BynderFeaturesContextValue>(
        () => ({
            features,
            isLoading,
            hasFeature: (feature: BynderPortalFeature) => features.includes(feature),
        }),
        [features, isLoading],
    );

    return <BynderFeaturesContext.Provider value={value}>{children}</BynderFeaturesContext.Provider>;
};

export const useBynderFeatures = (): BynderFeaturesContextValue => {
    const context = useContext(BynderFeaturesContext);

    if (!context) {
        throw new Error('useBynderFeatures must be used within a BynderFeaturesProvider');
    }

    return context;
};
